/**
 * Физика «жидкой» линзы таб-панели (14.09.2026, ролик заказчика).
 *
 * Отдельно от компонента, чтобы проверять тестами то, что глазом не
 * поймаешь: что линза доезжает, не болтается вечно и не разлетается после
 * паузы вкладки. Компонент только пишет результат в transform.
 */

/** ζ≈0.6: перелёт заметен, но за полсекунды гаснет. */
export const STIFFNESS = 380;
export const DAMPING = 24;
/** Набухание на нажатие — пружина жёстче, чтобы отзывалась сразу под пальцем. */
const PRESS_STIFFNESS = 520;
const PRESS_DAMPING = 30;
/** Больше шага не бывает: после паузы вкладки линза не улетает за экран. */
export const MAX_DT = 1 / 30;
/** Скорость, при которой вытягивание максимально, px/с. */
const FULL_SPEED = 2600;
/** Доля перелёта за крайнюю вкладку, на которую сдвигается вся панель. */
const BAR_PUSH = 0.35;
/** Сопротивление пальцу за краями панели. */
const RUBBER = 0.3;

export interface Lens {
  x: number;
  v: number;
  target: number;
  press: number;
  pressV: number;
  pressTarget: number;
}

export const lensAt = (x: number): Lens => ({ x, v: 0, target: x, press: 0, pressV: 0, pressTarget: 0 });

/** Шаг пружины на `dt` секунд. Меняет состояние на месте: так дешевле на кадр. */
export function step(s: Lens, dt: number): void {
  const t = Math.min(MAX_DT, Math.max(0, dt));
  s.v += (STIFFNESS * (s.target - s.x) - DAMPING * s.v) * t;
  s.x += s.v * t;
  s.pressV += (PRESS_STIFFNESS * (s.pressTarget - s.press) - PRESS_DAMPING * s.pressV) * t;
  s.press += s.pressV * t;
}

/** Всё на месте — кадры больше не нужны. */
export const settled = (s: Lens): boolean =>
  Math.abs(s.target - s.x) < 0.3 &&
  Math.abs(s.v) < 4 &&
  Math.abs(s.pressTarget - s.press) < 0.004 &&
  Math.abs(s.pressV) < 0.05;

/** Форма линзы: набухает от нажатия, вытягивается от скорости. */
export function lensShape(s: Lens): { sx: number; sy: number; speed: number } {
  const speed = Math.min(1, Math.abs(s.v) / FULL_SPEED);
  const grow = 1 + 0.14 * s.press;
  return { sx: grow * (1 + 0.42 * speed), sy: grow * (1 - 0.16 * speed), speed };
}

/** Сдвиг панели: перелёт линзы за крайнюю вкладку толкает её следом. */
export function barPush(x: number, min: number, max: number): number {
  const over = x < min ? x - min : x > max ? x - max : 0;
  return over * BAR_PUSH;
}

/** Куда вести линзу за пальцем: внутри — точно, за краями — с сопротивлением. */
export function rubber(px: number, min: number, max: number): number {
  if (px < min) return min + (px - min) * RUBBER;
  if (px > max) return max + (px - max) * RUBBER;
  return px;
}

/** Номер вкладки под точкой `x` (от левого края дорожки вкладок). */
export const slotAt = (x: number, slot: number, count: number): number =>
  Math.min(count - 1, Math.max(0, Math.floor(x / slot)));

/** Насколько линза над иконкой: 1 — ровно над ней, 0 — на соседней и дальше. */
export const nearness = (iconX: number, lensX: number, slot: number): number =>
  Math.max(0, 1 - Math.abs(iconX - lensX) / slot);
